import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Plus } from "lucide-react";
import {
  getShortlist,
  requestCustomAgent,
  type ShortlistAgent,
} from "@/services/shortlist-service";
import { AgentCard } from "./components/agent-card";
import { SelectOtherAgentCard } from "./components/select-other-agent-card";
import { SuggestCustomAgentDialog } from "./components/suggest-custom-agent-dialog";
import { Button } from "@/components/ui/button";
import icReact from "@/assets/icons/ic-react.svg";
import { useToast } from "@/hooks/use-toast";

const MAX_RECOMMENDED_AGENTS = 3;

const recommendationMessages: Record<number, string> = {
  3: "귀하의 요구사항을 이행하기 위해, 시스템 내부에서 상호작용하거나 외부 파트너로서 협업할 수 있는 3가지 핵심 AI 에이전트를 제안합니다.",
  2: "귀하의 요구사항을 이행하기 위해, 시스템 내부에서 상호작용하거나 외부 파트너로서 협업할 수 있는 2가지 핵심 AI 에이전트를 제안합니다. 추가 에이전트를 원하시려면 요청 양식을 제출해 주세요.",
  1: "귀하의 요구사항을 이행하기 위해, 시스템 내부에서 상호작용하거나 외부 파트너로서 협업할 수 있는 1가지 핵심 AI 에이전트를 제안합니다. 추가 에이전트를 원하시려면 요청 양식을 제출해 주세요.",
};

export default function ShortlistPage() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<ShortlistAgent[]>([]);
  const [projectName, setProjectName] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    getShortlist(id)
      .then((res) => {
        setAgents(res.data.data.matches ?? []);
        setProjectName(res.data.data.target_name ?? "");
      })
      .catch((err) => console.error("Failed to fetch shortlist:", err))
      .finally(() => setIsLoading(false));
  }, [id]);

  const recommendationContent = useMemo(() => {
    return (
      recommendationMessages[agents.length] ??
      "추가 에이전트를 원하시려면 요청 양식을 제출해 주세요."
    );
  }, [agents]);

  const handleBack = () => navigate(`/projects/${id}/report`);

  const handleViewAgent = (agentId: string) => navigate(`/agents/${agentId}`);

  const handleInviteToCompare = () => {
    if (selectedIds.size === 0) return;
    if (selectedIds.size < 2) {
      alert("2개 이상의 에이전트를 선택해주세요.");
      return;
    }
    const selectedAgents = agents.filter((a) => selectedIds.has(a.agent_id));
    navigate("/compare-agent", { state: { agents: selectedAgents, projectId: id } });
  };

  const handleToggleSelect = (agentId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(agentId) ? next.delete(agentId) : next.add(agentId);
      return next;
    });
  };

  const handleSelectOther = () => setIsDialogOpen(true);

  const handleSubmitCustomAgent = async (title: string, content: string) => {
    try {
      await requestCustomAgent({
        title,
        content,
        agent_ids: [],
        project_id: id,
      });

      toast({ description: "요청이 전송되었습니다." });
      setIsDialogOpen(false);
    } catch (error) {
      toast({
        description: "오류가 발생했습니다. 다시 시도해 주세요.",
        variant: "destructive",
      });
    }
  };



  const hasAgents = agents.length > 0;

  // Build cards: agents + "select other" placeholder to fill up to MAX_RECOMMENDED_AGENTS
  const agentCards = agents
    .slice(0, MAX_RECOMMENDED_AGENTS)
    .map((agent, index) => (
      <AgentCard
        key={agent.agent_id}
        agent={agent}
        index={index + 1}
        isSelected={selectedIds.has(agent.agent_id)}
        onToggleSelect={handleToggleSelect}
        onView={() => handleViewAgent(agent.agent_id)}
      />
    ));

  if (agentCards.length < MAX_RECOMMENDED_AGENTS) {
    agentCards.push(<SelectOtherAgentCard key="select-other" onClick={handleSelectOther} />);
  }

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="flex flex-col max-w-[1120px] px-5 min-[1120px]:px-0 mx-auto w-full"
        style={{ display: isLoading ? "none" : "flex" }}
      >
      {/* Header */}
      <div className="w-full flex justify-between items-center gap-[16px] mt-[40px]">
        <button
          type="button"
          onClick={handleBack}
          className="flex h-12 w-12 items-center justify-center"
        >
          <ChevronLeft className="h-6 w-6 text-[#202020]" />
        </button>
        <div className="flex flex-1 flex-col gap-[4px]">
          <h1 className="text-xl font-bold text-[#202020]">
            {projectName || "Short-list 질의 내용"}
          </h1>
          <span className="text-[14px] text-[#646464]">
            귀하의 프로젝트에 가장 적합한 에이전트 {agents.length}개를 찾았습니다
          </span>
        </div>
        <Button
          className="h-[48px] rounded-[1000px]"
          variant={"default"}
          onClick={handleInviteToCompare}
          disabled={selectedIds.size === 0}
        >
          {selectedIds.size === 1 ? "상세 보기" : "선택한 에이전트 비교"}
        </Button>
      </div>

      {!hasAgents ? (
        /* Empty State */
        <div className="flex flex-col gap-[24px] mt-[48px]">
          <div className="flex flex-col gap-[4px]">
            <h3 className="text-[20px] font-bold text-[#202020]">추천 에이전트</h3>
            <p className="text-[14px] text-[#646464] whitespace-pre-line">
              {`귀하에게 적합한 에이전트를 찾지 못한 것 같습니다. \n'다른 에이전트 요청' 버튼을 클릭한 후, 원하시는 에이전트에 대해 자세히 설명해 주시면 적절한 에이전트를 찾는 데 도움을 드리겠습니다.`}
            </p>
          </div>
          <div
            className="rounded-[12px] border border-[#00000017] py-[72.5px] px-24 cursor-pointer hover:border-[#FF3B00]/50 transition-colors"
            onClick={() => setIsDialogOpen(true)}
          >
            <div className="flex flex-col justify-center items-center gap-[32px]">
              <div className="w-[80px] h-[80px] rounded-full bg-[#00000010] flex justify-center items-center">
                <Plus width={"26.67px"} height={"26.67px"} />
              </div>
              <div className="flex flex-col justify-center items-center gap-[12px]">
                <h3 className="text-[20px] font-bold text-[#202020]">
                  다른 에이전트가 필요하신가요?
                </h3>
                <p className="max-w-[257px] text-center text-[16px] text-[#646464]">
                  원하는 에이전트를 찾지 못하셨나요? 당신의 요구에 딱 맞는 맞춤형 AI 에이전트를
                  요청해 보세요.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Divider */}
          <div className="w-full h-0 border border-[#00000017] my-[24px]" />

          {/* Title */}
          <div className="flex items-center gap-[12px]">
            <div className="w-[3px] self-stretch bg-[#FF3B00]" />
            <h3 className="text-[20px] font-bold text-[#202020]">
              멀티모달 기반 지능형 보고드 인텔리전스 에이전트 도입 가획안
            </h3>
          </div>

          {/* AI Recommendation Banner */}
          <div
            className="mt-[48px] p-[16px] flex flex-col gap-[16px]"
            style={{
              border: "1px solid transparent",
              borderRadius: "8px",
              borderImage: "linear-gradient(91.37deg, #FD5108 0.32%, #FC9CBD 55.03%) 1",
            }}
          >
            <div className="flex justify-start items-center gap-[12px]">
              <div className="p-[8px] bg-[#FF80131C] rounded">
                <img src={icReact} alt="" />
              </div>
              <h3 className="text-[20px] text-[#202020]">AI Recommendation</h3>
            </div>
            <div className="text-[14px] text-[#202020]">{recommendationContent}</div>
          </div>

          {/* Agent Cards Section */}
          <div className="mt-[48px] flex flex-col gap-[12px]">
            <div className="flex items-center gap-[12px]">
              <div className="w-[3px] self-stretch bg-[#FF3B00]" />
              <div className="flex flex-col gap-[4px]">
                <h3 className="text-[20px] font-bold text-[#202020]">추천 에이전트</h3>
                <p className="text-[14px] text-[#646464]">
                  2개 이상의 에이전트를 선택하면 비교할 수 있어요.
                </p>
              </div>
            </div>
            <div className="mt-[24px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[16px]">
              {agentCards}
            </div>
          </div>
        </>
      )}
      {/* Dialog */}
      <SuggestCustomAgentDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleSubmitCustomAgent}
      />
    </div>
    </>
  );
}
